import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../hooks/useLanguage';
import { Star, Ship, Waves, ArrowRight } from 'lucide-react';
import OptimizedImage from './OptimizedImage';
import { defaultHeroImage } from '../lib/imageSrc';

const HERO_WIDTH = 1536;
const HERO_HEIGHT = 1024;
const DEFAULT_HERO = defaultHeroImage();

const getYouTubeId = (value) => {
  if (!value) return null;
  const match = value.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : (value.length === 11 ? value : null);
};


const Hero = ({ onSearch }) => {
  const { t } = useLanguage();
  const [heroMedia, setHeroMedia] = useState({ type: 'image', image_url: DEFAULT_HERO, youtube_url: '', start_time: null, end_time: null, play_once: false, play_on_mobile: true, fallback_image: DEFAULT_HERO });
  const ytPlayerRef = useRef(null);
  const ytIntervalRef = useRef(null);
  const ytContainerRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    axios.get(`${API_URL}/api/hero-media`)
      .then(res => {
        const hm = res.data;
        if (!hm?.type) return;
        setHeroMedia({
          type: hm.type,
          image_url: hm.image_url || (hm.type === 'image' ? hm.value : '') || DEFAULT_HERO,
          youtube_url: hm.youtube_url || (hm.type === 'youtube' ? hm.value : '') || '',
          start_time: hm.start_time ?? null,
          end_time: hm.end_time ?? null,
          play_once: hm.play_once || false,
          play_on_mobile: hm.play_on_mobile !== false,
          fallback_image: hm.fallback_image || DEFAULT_HERO,
        });
      })
      .catch(() => {});
  }, [API_URL]);

  const handleTileClick = (type) => {
    onSearch({ resourceType: type, persons: 1 });
    const element = document.getElementById('offers');
    if (!element) return;
    const offsetPosition = element.getBoundingClientRect().top + window.pageYOffset - 80;
    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
  };

  const youtubeId = heroMedia.type === 'youtube' ? getYouTubeId(heroMedia.youtube_url) : null;
  const isMobileDevice = /Mobi|Android/i.test(navigator.userAgent);
  const playOnMobile = heroMedia.play_on_mobile !== false;
  const showVideo = youtubeId && (!isMobileDevice || playOnMobile);
  const fallbackSrc = heroMedia.fallback_image || DEFAULT_HERO;
  const imageSrc = heroMedia.type === 'image' ? (heroMedia.image_url || DEFAULT_HERO) : fallbackSrc;
  const startTime = heroMedia.start_time != null ? Number(heroMedia.start_time) : 0;
  const endTime = heroMedia.end_time != null ? Number(heroMedia.end_time) : null;
  const playOnce = heroMedia.play_once || false;

  useEffect(() => {
    if (!showVideo || !youtubeId) return;

    const containerId = 'yt-hero-player';

    const initPlayer = () => {
      if (ytPlayerRef.current) {
        ytPlayerRef.current.destroy();
        ytPlayerRef.current = null;
      }
      if (ytIntervalRef.current) {
        clearInterval(ytIntervalRef.current);
        ytIntervalRef.current = null;
      }

      ytPlayerRef.current = new window.YT.Player(containerId, {
        videoId: youtubeId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          showinfo: 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          start: startTime,
        },
        events: {
          onReady: (e) => {
            e.target.playVideo();
            if (endTime != null) {
              ytIntervalRef.current = setInterval(() => {
                const player = ytPlayerRef.current;
                if (!player || typeof player.getCurrentTime !== 'function') return;
                const current = player.getCurrentTime();
                if (current >= endTime) {
                  if (playOnce) {
                    player.pauseVideo();
                    clearInterval(ytIntervalRef.current);
                  } else {
                    player.seekTo(startTime, true);
                  }
                }
              }, 500);
            }
          },
          onStateChange: (e) => {
            if (e.data === window.YT.PlayerState.ENDED) {
              if (!playOnce) {
                e.target.seekTo(startTime, true);
                e.target.playVideo();
              }
            }
          },
        },
      });
    };

    if (window.YT && window.YT.Player) {
      initPlayer();
    } else {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(tag);
      window.onYouTubeIframeAPIReady = initPlayer;
    }

    return () => {
      if (ytIntervalRef.current) clearInterval(ytIntervalRef.current);
      if (ytPlayerRef.current) {
        ytPlayerRef.current.destroy();
        ytPlayerRef.current = null;
      }
    };
  }, [showVideo, youtubeId, startTime, endTime, playOnce]);

  return (
    <div className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
      {/* Background with Overlay */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-950/50 to-primary-950/80 z-10"></div>
        {showVideo ? (
          <div
            id="yt-hero-player"
            ref={ytContainerRef}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            style={{ width: '177.78vh', height: '100vh', minWidth: '100%', minHeight: '56.25vw' }}
          />
        ) : (
          <OptimizedImage
            src={imageSrc}
            alt="Luxury Boat Rental Costa del Sol"
            width={HERO_WIDTH}
            height={HERO_HEIGHT}
            loading="eager"
            fetchPriority="high"
            className="w-full h-full object-cover animate-scale-in"
          />
        )}
      </div>

      {/* Content */}
      <div className="container relative z-20 px-4 text-center text-white">
        <div className="animate-fade-in space-y-6 max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="flex text-accent-400">
              {[1, 2, 3, 4, 5].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-current" />
              ))}
            </div>
            <span className="text-sm font-medium tracking-wide bg-white/10 px-3 py-1 rounded-full backdrop-blur-sm">
              {t('hero.badge')}
            </span>
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight">
            {t('hero.headline')}
          </h1>

          <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
            {t('hero.subheadline')}
          </p>

          {/* Category Tiles */}
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center items-center">

            {/* Boat tile */}
            <button
              onClick={() => handleTileClick('boat')}
              className="group relative w-full sm:w-64 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/25 hover:border-white/50 rounded-2xl p-6 shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-accent-500/20 active:scale-95 text-left overflow-hidden"
            >
              {/* Subtle gradient accent on hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-accent-500/0 to-accent-500/0 group-hover:from-accent-500/10 group-hover:to-transparent rounded-2xl transition-all duration-300" />
              <div className="relative flex items-center gap-4">
                <div className="flex-shrink-0 w-14 h-14 bg-white/15 group-hover:bg-accent-500/30 rounded-xl flex items-center justify-center transition-all duration-300">
                  <Ship className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-white/60 uppercase tracking-widest mb-0.5">{t('search.resourceType')}</p>
                  <p className="text-xl font-bold text-white leading-tight">{t('search.boat')}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-white/50 group-hover:text-accent-400 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0" />
              </div>
            </button>

            {/* Jet Ski tile */}
            <button
              onClick={() => handleTileClick('jetski')}
              className="group relative w-full sm:w-64 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/25 hover:border-white/50 rounded-2xl p-6 shadow-2xl transition-all duration-300 hover:scale-105 hover:shadow-accent-500/20 active:scale-95 text-left overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-accent-500/0 to-accent-500/0 group-hover:from-accent-500/10 group-hover:to-transparent rounded-2xl transition-all duration-300" />
              <div className="relative flex items-center gap-4">
                <div className="flex-shrink-0 w-14 h-14 bg-white/15 group-hover:bg-accent-500/30 rounded-xl flex items-center justify-center transition-all duration-300">
                  <Waves className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-white/60 uppercase tracking-widest mb-0.5">{t('search.resourceType')}</p>
                  <p className="text-xl font-bold text-white leading-tight">{t('search.jetski')}</p>
                </div>
                <ArrowRight className="w-5 h-5 text-white/50 group-hover:text-accent-400 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0" />
              </div>
            </button>

          </div>

        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce z-20 text-white/70">
        <div className="w-6 h-10 border-2 border-current rounded-full flex justify-center p-1">
          <div className="w-1 h-2 bg-current rounded-full animate-scroll"></div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
